// src/components/ExportModal/index.jsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  DatePicker,
  Radio,
  Segmented,
  Checkbox,
  Button,
  message,
} from "antd";
import {
  DownloadOutlined,
  CloseOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { ExportIcon } from "../../assets/svgs/icons";
import { LuX } from "react-icons/lu";
import ExportReportModal from "./ExportReportModal";
import {
  useGetCampaignsQuery,
  useGetFormsQuery,
  useGetInterpretersQuery,
  useGetOrganizationsQuery,
} from "../../store/api/apiSlice";
import { useSelector } from "react-redux";
import {
  selectCurrentRole,
  selectCurrentUser,
} from "../../store/auth/authSlice";
import moment from "moment";
import { getCampaignStatusFromForms } from "../../utils/campaigns";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

applyPlugin(jsPDF);

const ExportModal = ({ visible, onClose }) => {
  const [reportType, setReportType] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportTypes, setReportTypes] = useState([]);
  const [fileFormat, setFileFormat] = useState("PDF");
  const [exporting, setExporting] = useState(false);
  const [viewType, setViewType] = useState("Analytics");
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // Get current user role and organization ID
  const userRole = useSelector(selectCurrentRole);
  const currentUser = useSelector(selectCurrentUser);
  const organizationId = currentUser?.organization?._id;

  // Query data with optional date range and organization filter for organization users
  const dateParams = {
    ...(startDate && endDate
      ? {
          start_date: startDate.unix(),
          end_date: endDate.unix(),
        }
      : {}),
    ...(organizationId && userRole === "organization"
      ? { organization: organizationId }
      : {}),
  };

  // Get all necessary data
  // const { data: formsData } = useGetFormsQuery(dateParams);
  const {
    data: formsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetFormsQuery(
    {
      search: "",
      // status: filters.status,
      // type: filters.type,
      // start_date: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
      // end_date: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
    },
    {
      // Skip preloading forms for interpreter role to avoid unnecessary call
      skip: userRole === "interpreter",
    }
  );
  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    refetch: refetchCampaigns,
  } = useGetCampaignsQuery();
  const { data: interpretersData } = useGetInterpretersQuery(dateParams);
  const { data: organizationsData } = useGetOrganizationsQuery(
    userRole === "organization"
      ? { ...dateParams, _id: organizationId }
      : dateParams
  );

  const handleStartDateChange = (date) => setStartDate(date);
  const handleEndDateChange = (date) => setEndDate(date);

  const handleReportTypeChange = (checkedValue) => {
    if (reportTypes.includes(checkedValue)) {
      setReportTypes(reportTypes.filter((type) => type !== checkedValue));
    } else {
      setReportTypes([...reportTypes, checkedValue]);
    }
  };

  // Filter forms based on selected campaign and organization
  const getFilteredForms = () => {
    if (!formsData?.data) return [];

    let filteredData = [...formsData.data];

    // Filter by organization if logged in as organization
    if (userRole === "organization" && organizationId) {
      filteredData = filteredData.filter(
        (form) =>
          form.organization?._id === organizationId ||
          form.organization === organizationId
      );
    }

    // If no campaign is selected, return the current filtered data
    if (!selectedCampaign) {
      return filteredData;
    }

    // Find the selected campaign
    const campaign = campaignsData?.data?.find(
      (c) => c._id === selectedCampaign
    );

    // If campaign not found or has no forms, return empty array
    if (!campaign || !campaign.forms || campaign.forms.length === 0) {
      return [];
    }

    // Extract form IDs from the campaign
    const campaignFormIds = campaign.forms.map((form) =>
      typeof form === "object" ? form._id : form
    );

    // Filter forms by campaign form IDs
    return filteredData.filter((form) => campaignFormIds.includes(form._id));
  };

  const getSelectedData = () => {
    let exportData = {};

    // Define table headers for each report type
    const tableHeaders = {
      forms: [
        "Title",
        "Organisation",
        "Questions",
        "Responses",
        "Status",
        "Created Date",
      ],
      campaigns: [
        "Title",
        "Organisations",
        "Forms",
        "Status",
      ],
      interpreters: [
        "Name",
        "Email",
        "Completed Assignments",
        "Total Assignments",
        "Status",
        "Joined Date",
      ],
      organizations: [
        "Name",
        "Location",
        "Campaigns",
        "Forms",
        "Users",
        "Status",
      ],
    };

    // Get filtered forms based on selected campaign
    const filteredForms = getFilteredForms();

    if (reportTypes.includes("formReport") || reportTypes.length === 0) {
      exportData.forms = {
        headers: tableHeaders.forms,
        data:
          filteredForms.map((form) => [
            form.title,
            form.organization?.name || "N/A",
            form.questions?.length || 0,
            form.responses?.length || 0,
            form.status,
            moment(form.created_at).format("YYYY-MM-DD"),
          ]) || [],
      };
    }

    // Filter campaigns by organization if needed
    let filteredCampaigns = campaignsData?.data || [];
    if (userRole === "organization" && organizationId) {
      filteredCampaigns = filteredCampaigns.filter((campaign) =>
        campaign.organizations?.some(
          (org) => org._id === organizationId || org === organizationId
        )
      );
    }

    if (reportTypes.includes("campaignReport") || reportTypes.length === 0) {
      exportData.campaigns = {
        headers: tableHeaders.campaigns,
        data:
          filteredCampaigns.map((campaign) => [
            campaign.title,
            campaign.organizations
              ?.map((org) => (typeof org === "object" ? org.name : "N/A"))
              .join(", "),
            campaign.forms?.length || 0,
            getCampaignStatusFromForms(campaign),
          ]) || [],
      };
    }

    // Filter interpreters by organization if needed
    let filteredInterpreters = interpretersData?.data || [];
    if (userRole === "organization" && organizationId) {
      filteredInterpreters = filteredInterpreters.filter(
        (interpreter) =>
          interpreter.organization?._id === organizationId ||
          interpreter.organization === organizationId
      );
    }

    if (reportTypes.includes("interpreterReport") || reportTypes.length === 0) {
      exportData.interpreters = {
        headers: tableHeaders.interpreters,
        data:
          filteredInterpreters.map((interpreter) => [
            interpreter.user?.full_name,
            interpreter.user?.email,
            interpreter.completed_assignments || 0,
            interpreter.total_assignments || 0,
            interpreter.status,
            moment(interpreter.created_at).format("YYYY-MM-DD"),
          ]) || [],
      };
    }

    // For organization users, only include their own organization
    if (
      reportTypes.includes("organizationReport") ||
      reportTypes.length === 0
    ) {
      let orgsData = organizationsData?.data || [];

      if (userRole === "organization" && organizationId) {
        orgsData = orgsData.filter((org) => org._id === organizationId);
      }

      exportData.organizations = {
        headers: tableHeaders.organizations,
        data:
          orgsData.map((org) => [
            org.name,
            org.location?.name || "N/A",
            org.campaigns_count || 0,
            org.forms_count || 0,
            org.users_count || 0,
            org.status,
          ]) || [],
      };
    }

    return exportData;
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF();
    const dateRange =
      startDate && endDate
        ? `Date Range: ${startDate.format("YYYY-MM-DD")} to ${endDate.format(
            "YYYY-MM-DD"
          )}`
        : "All Time Data";

    // Add title and date range
    doc.setFontSize(16);
    doc.text("Analytics Report", 14, 15);
    doc.setFontSize(10);
    doc.text(dateRange, 14, 25);

    let yPosition = 35;

    Object.entries(data).forEach(([section, { headers, data }]) => {
      // Skip sections with no data
      if (data.length === 0) return;

      // Add section title
      doc.setFontSize(12);
      doc.text(
        `${section.charAt(0).toUpperCase() + section.slice(1)} Report`,
        14,
        yPosition
      );

      // Add table
      doc.autoTable({
        head: [headers],
        body: data,
        startY: yPosition + 5,
        headStyles: {
          fillColor: [24, 24, 27],
          textColor: [255, 255, 255],
          fontSize: 10,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        theme: "grid",
        margin: { left: 14, right: 14 },
        didDrawPage: function (data) {
          // Update yPosition for next section
          yPosition = data.cursor.y + 15;

          // Add new page if there's not enough space
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
        },
      });
    });

    doc.save(`analytics_report_${moment().format("YYYY-MM-DD")}.pdf`);
  };

  const exportToExcel = (data) => {
    const wb = XLSX.utils.book_new();

    Object.entries(data).forEach(([section, { headers, data }]) => {
      // Skip sections with no data
      if (data.length === 0) return;

      const wsData = [headers, ...data];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, section);
    });

    XLSX.writeFile(wb, `report_${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  const exportToCSV = (data) => {
    Object.entries(data).forEach(([section, { headers, data }]) => {
      // Skip sections with no data
      if (data.length === 0) return;

      const csvContent = [
        headers.join(","),
        ...data.map((row) =>
          row
            .map((cell) =>
              typeof cell === "string" && cell.includes(",")
                ? `"${cell}"`
                : cell
            )
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${section}_report_${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const data = getSelectedData();

      // Check if there's any data to export
      const hasData = Object.values(data).some(
        (section) => section.data.length > 0
      );

      if (!hasData) {
        message.warning("No data available to export");
        setExporting(false);
        return;
      }

      switch (fileFormat) {
        case "PDF":
          exportToPDF(data);
          break;
        case "Excel":
          exportToExcel(data);
          break;
        case "CSV":
          exportToCSV(data);
          break;
      }

      message.success("Report exported successfully");
      onClose();
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      className="export-modal"
    >
      <ExportReportModal
        isNotModal={true}
        selectedCampaignProp={selectedCampaign}
        setSelectedCampaignProp={setSelectedCampaign}
        organizationId={
          userRole === "organization" ? organizationId : undefined
        }
      />
    </Modal>
  );
};

export default ExportModal;
